import {
  useAppDispatch,
  useAppSelector,
  setScheduleSlots,
  setDeliveryError,
  setOrderInfoLoading,
  setFulfillmentToTimeError,
} from '@/app/store';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { PointInputDto } from '@/app/store/api/restApi';
import { getOrderItemsFromMenuItems, useChangePersonalInfoDelivery } from '@/features';
import { getLocalizedReduxErrorMessage, getYandexLang, parseAddressFromGeocoder } from '@/shared/lib';
import {
  AddressSuggestion,
  generateTimeIntervals,
  useLazyGetGeocodeQuery,
  useGetOptimalDeliveryZoneMutation,
  useLazyGetAddressSuggestionsQuery,
} from '@/entities';

export const useGetDeliveryZone = ({
  inCatalog,
  onSuccess,
  isOrderAnotherPerson = false,
}: {
  inCatalog: boolean;
  onSuccess: VoidFunction;
  isOrderAnotherPerson: boolean;
}) => {
  const [getOptimalDeliveryZone] = useGetOptimalDeliveryZoneMutation();
  const [triggerGeocode] = useLazyGetGeocodeQuery();
  const [triggerSuggest] = useLazyGetAddressSuggestionsQuery();
  const { t, i18n } = useTranslation('validation');
  const { t: tErrors } = useTranslation('errors');
  const { t: tBasket } = useTranslation('basket');

  const {
    delivery,
    onSetDeliveryZone,
    onClearDeliveryZone,
    onSetNationwideInfo,
    onSetDeliveryAddress,
    onClearDeliveryAddress,
    onClearDeliveryAddressDetails,
  } = useChangePersonalInfoDelivery(isOrderAnotherPerson);

  const dispatch = useAppDispatch();
  const { totalCheck, products, unavailableProductIds } = useAppSelector((state) => state.basket);
  const { fulfillmentToTime } = useAppSelector((state) => state.order);

  const orderedItems = useMemo(
    () => getOrderItemsFromMenuItems(products, unavailableProductIds),
    [products, unavailableProductIds],
  );

  const [inputedAddress, setInputedAddress] = useState(delivery.address ?? '');

  const [debouncedValue] = useDebounce(inputedAddress, 1000);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  useEffect(() => {
    const func = async () => {
      if (!debouncedValue.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await triggerSuggest({
          text: debouncedValue,
          lang: getYandexLang(i18n.language),
          ll: delivery.cityCoords ? `${delivery.cityCoords.lng},${delivery.cityCoords.lat}` : undefined,
        }).unwrap();

        setSuggestions(response);
      } catch (error) {
        toast.error(getLocalizedReduxErrorMessage(error ?? '', tErrors));
        dispatch(setDeliveryError(t('delivery.invalidAddress')));
      }
    };

    func();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, dispatch]);

  const handleSuggestionSelect = async (suggestion: AddressSuggestion) => {
    setSuggestions([]);
    dispatch(setOrderInfoLoading(true));

    try {
      // Fetch coordinates using the URI
      const geocodeData = await triggerGeocode({
        uri: suggestion.uri,
        lang: getYandexLang(i18n.language),
      }).unwrap();

      if (!geocodeData.response || !geocodeData.response.GeoObjectCollection) {
        onClearDeliveryZone();
        onClearDeliveryAddress();
        dispatch(setDeliveryError(t('delivery.invalidAddress')));
        if (inCatalog) toast.error(t('delivery.invalidAddress'));
      } else {
        const geocodeObject = geocodeData.response.GeoObjectCollection.featureMember[0].GeoObject;
        // Extract coordinates from the geocoder response
        const coords = geocodeObject.Point.pos.split(' ');
        const lat = parseFloat(coords[1]);
        const lng = parseFloat(coords[0]);

        const currentFullAddress = suggestion.value; // Use value as full address for now

        setInputedAddress(currentFullAddress);

        const point: PointInputDto = { lat, lng };

        const { city, street, house } = parseAddressFromGeocoder(geocodeObject);

        if (currentFullAddress !== delivery.address) {
          onClearDeliveryAddressDetails();
          onSetDeliveryAddress({ address: currentFullAddress, coordinates: point, city, street, house });
        }

        const zone = await getOptimalDeliveryZone({
          getOptimalDeliveryZoneDto: {
            point,
            orderPrice: totalCheck,
            date: fulfillmentToTime,
            timeSlotsAdjustmentInfo: orderedItems.length > 0 ? { orderedItems } : undefined,
          },
        }).unwrap();

        if (zone.data) {
          if (zone.data.nationwideInfo) {
            const nwLocation = zone.data.nationwideInfo.location;
            const nwDeliveryPrice = zone.data.nationwideInfo.deliveryPrice;
            const geocodeData = await triggerGeocode({
              geocode: `${nwLocation.coordinates.lng},${nwLocation.coordinates.lat}`,
            }).unwrap();
            const geocodeObject = geocodeData.response.GeoObjectCollection.featureMember[0].GeoObject;
            const { city } = parseAddressFromGeocoder(geocodeObject);

            dispatch(setDeliveryError(undefined));
            onSetDeliveryZone({
              price: undefined,
              zoneId: undefined,
              locationId: undefined,
              paymentTypes: nwLocation.delivery?.paymentTypes ?? [],
              onlinePayment: nwLocation.delivery?.onlinePayment ?? undefined,
            });

            onSetNationwideInfo({ locationId: nwLocation.id, city, price: nwDeliveryPrice });
          } else {
            onSetNationwideInfo(undefined);
            const mergedSchedule = zone.data.mergedSchedule;

            if (mergedSchedule.dailyTimeslots.length === 0) {
              dispatch(setScheduleSlots([]));
              if (!zone.data.mergedSchedule.asapAvailable) {
                dispatch(setDeliveryError(t('delivery.zoneUnavailable')));
                if (inCatalog) toast.error(t('delivery.zoneUnavailable'));
              } else dispatch(setDeliveryError(undefined));
            } else {
              dispatch(setDeliveryError(undefined));
              dispatch(
                setScheduleSlots(
                  generateTimeIntervals(mergedSchedule.dailyTimeslots, mergedSchedule.asapAvailable, tBasket) ?? [],
                ),
              );
            }

            if (zone.data.deliveryZone) {
              dispatch(setFulfillmentToTimeError(undefined));
              onSetDeliveryZone({
                zoneId: zone.data.deliveryZone.deliveryZone.id,
                locationId: zone.data.deliveryZone.location.id,
                price: zone.data.deliveryZone.deliveryPrice ?? 0,
                paymentTypes: zone.data.deliveryZone.availablePaymentTypes,
                onlinePayment: zone.data.deliveryZone.location.delivery?.onlinePayment ?? undefined,
              });
            } else {
              onClearDeliveryZone();
              if (mergedSchedule.dailyTimeslots.length > 0)
                dispatch(setFulfillmentToTimeError(t('fulfillment.dateTimeRequired')));
              else dispatch(setFulfillmentToTimeError(t('fulfillment.whenClosed')));
            }
          }
        }
      }
    } catch (error) {
      toast.error(getLocalizedReduxErrorMessage(error ?? '', tErrors));
      dispatch(setDeliveryError(t('delivery.invalidAddress')));
    } finally {
      onSuccess();
      dispatch(setOrderInfoLoading(false));
    }
  };

  useEffect(() => {
    if (delivery.address && delivery.coords) setInputedAddress(delivery.address);
  }, [delivery.address, delivery.coords]);

  return { handleSuggestionSelect, suggestions, inputedAddress, setInputedAddress };
};
